import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import NavBar from '../components/NavBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Image as ImageIcon, Download, Ruler, Loader2 } from 'lucide-react';
import api, { formatApiError, toolsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';

export default function ImageResize() {
  const { user, refreshUser } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [width, setWidth] = useState(60);
  const [height, setHeight] = useState(90);
  const [loading, setLoading] = useState(false);
  const [resultUrl, setResultUrl] = useState(null);
  const [settings, setSettings] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    toolsAPI.getResizeSettings().then(r => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  useEffect(() => () => {
    if (resultUrl) URL.revokeObjectURL(resultUrl);
  }, [resultUrl]);

  const onFile = useCallback((f) => {
    if (!f) return;
    if (!['image/jpeg','image/png','image/webp','image/jpg'].includes(f.type)) {
      toast.error('Sadece JPG, PNG veya WebP yükleyin'); return;
    }
    if (f.size > 25 * 1024 * 1024) { toast.error('Dosya 25 MB üzeri olamaz'); return; }
    setFile(f);
    setResultUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    setPreview(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(f); });
  }, []);

  const submit = async () => {
    if (!file) { toast.error('Önce bir görsel yükleyin'); return; }
    if (!width || !height || width <= 0 || height <= 0) { toast.error('Geçerli ölçü girin'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('width', String(width));
      fd.append('height', String(height));
      fd.append('unit', 'cm');
      const res = await api.post('/tools/image-resize', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob',
      });
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      setResultUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers?.['content-disposition'];
      const match = cd?.match(/filename="?([^"]+)"?/);
      a.download = match?.[1] || `numune_${width}x${height}cm.png`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`Numune hazır: ${width}cm × ${height}cm`);
      if (settings?.paid) refreshUser();
    } catch (err) {
      try {
        const txt = await err?.response?.data?.text?.();
        const j = txt && JSON.parse(txt);
        toast.error(j?.detail || 'Beklenmeyen hata');
      } catch {
        toast.error(formatApiError(err));
      }
    } finally { setLoading(false); }
  };

  const isFree = !settings?.paid;
  const price = settings?.price_tl || 0;
  const balance = user?.balance_tl || 0;
  const cantAfford = !isFree && balance < price;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <NavBar />
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
            <Ruler className="w-7 h-7 text-fuchsia-400" /> Ölçü Numune Aracı
            {isFree
              ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 ml-1">Ücretsiz</span>
              : <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 ml-1">{price.toFixed(2)} TL / kullanım</span>}
          </h1>
          <p className="text-muted-foreground">Görseli yükle, baskı/üretim ölçülerini santimetre olarak yaz. Görselin yanlarına ölçü okları ve cm yazıları eklenmiş bir <strong>numune görseli</strong> indirilir.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* LEFT: Inputs */}
          <div className="glass-card rounded-2xl p-6">
            <h2 className="font-semibold mb-3">1. Görsel Yükle</h2>
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e)=>{ e.preventDefault(); }}
              onDrop={(e)=>{ e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
              className="border-2 border-dashed border-white/15 rounded-xl p-6 text-center cursor-pointer hover:border-fuchsia-500/50 transition-colors mb-5"
              data-testid="resize-dropzone"
            >
              {preview ? (
                <img src={preview} alt="preview" className="max-h-48 mx-auto rounded-lg" />
              ) : (
                <div className="text-muted-foreground py-3">
                  <ImageIcon className="w-10 h-10 mx-auto mb-2" />
                  <p className="text-sm">Tıkla veya sürükleyip bırak</p>
                  <p className="text-xs mt-1">JPG, PNG, WebP · max 25 MB</p>
                </div>
              )}
              <input ref={inputRef} type="file" className="hidden" accept="image/jpeg,image/png,image/webp" onChange={(e) => onFile(e.target.files?.[0])} data-testid="resize-file-input" />
            </div>

            <h2 className="font-semibold mb-3">2. Ölçüleri Yaz</h2>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <Label htmlFor="w">Genişlik (cm)</Label>
                <Input id="w" type="number" step="0.5" min="0.5" max="1000" value={width} onChange={(e)=>setWidth(Number(e.target.value)||0)} className="bg-white/5 border-white/10 mt-1" data-testid="resize-width-input" />
              </div>
              <div>
                <Label htmlFor="h">Yükseklik (cm)</Label>
                <Input id="h" type="number" step="0.5" min="0.5" max="1000" value={height} onChange={(e)=>setHeight(Number(e.target.value)||0)} className="bg-white/5 border-white/10 mt-1" data-testid="resize-height-input" />
              </div>
            </div>

            {!isFree && (
              <p className="text-xs mb-3 text-amber-400">Bu işlem {price.toFixed(2)} TL ücretlidir. Mevcut bakiye: {balance.toFixed(2)} TL</p>
            )}

            <Button onClick={submit} disabled={!file || loading || cantAfford} className="w-full bg-gradient-to-r from-fuchsia-500 to-indigo-500 hover:from-fuchsia-600 hover:to-indigo-600" data-testid="resize-submit-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Numune oluşturuluyor...</> : <><Download className="w-4 h-4 mr-2" /> Numuneyi Oluştur ve İndir</>}
            </Button>
          </div>

          {/* RIGHT: Result preview */}
          <div className="glass-card rounded-2xl p-6 flex flex-col">
            <h2 className="font-semibold mb-3">Numune Önizleme</h2>
            {resultUrl ? (
              <div className="flex-1 flex flex-col items-center justify-center bg-black/30 rounded-xl p-3 border border-white/10">
                <img src={resultUrl} alt="numune" className="max-w-full max-h-[500px] rounded-md" data-testid="resize-result-img" />
                <a href={resultUrl} download={`numune_${width}x${height}cm.png`} className="mt-3 text-xs text-fuchsia-400 hover:text-fuchsia-300">İndirilen numuneyi tekrar kaydet</a>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground border-2 border-dashed border-white/10 rounded-xl py-12">
                <Ruler className="w-10 h-10 mb-3" />
                <p className="text-sm">Görsel yükle ve ölçüleri yaz, numuneyi burada gör</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
